import { localize } from '../i18n';
import SingleLineText from './ui/SingleLineText';
import React, { useRef, useEffect } from 'react';
import { Box, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import AppButton from './ui/AppButton';

const TOP_PADDING = '8px';

export default function WizardLayout({
    step, totalSteps, headline, subtitle,
    ctaLabel = localize('다음'), ctaDisabled = false, ctaHidden = false,
    onBack, onNext, direction = 'forward', children,
    closeOnHeader = false,
}) {
    const scrollRef = useRef(null);

    useEffect(() => {
        scrollRef.current?.scrollTo(0, 0);
        window.scrollTo(0, 0);
    }, [step]);

    return (
        // 100vh는 iOS WebView/iPad에서 visible viewport보다 커서 CTA가 화면 밖으로 밀림.
        // 100dvh는 현재 실제 viewport에 맞춰져 모든 화면에서 CTA가 한 페이지에 노출됨.
        <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
            {/* Header - fixed */}
            <Box sx={{
                position: 'fixed', top: 0, left: '50%', transform: 'translateX(-50%)',
                width: '100%', maxWidth: 480, zIndex: 100,
                pt: TOP_PADDING, px: 1.5, pb: 1.5, bgcolor: '#fff',
            }}>
                <IconButton
                    aria-label={closeOnHeader ? localize("닫기") : localize("뒤로가기")}
                    onClick={onBack}
                    onMouseDown={(e) => e.preventDefault()}
                    sx={{ color: '#333', width: 48, height: 48, p: 0, ml: -0.5 }}
                >
                    {closeOnHeader ? <CloseIcon /> : <ArrowBackIcon />}
                </IconButton>
                <Box sx={{ px: 1.5, mt: 0.5 }}>
                    <SingleLineText sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#1a1a1a', lineHeight: 1.3 }}>
                        {headline}
                    </SingleLineText>
                    {subtitle && (
                        <SingleLineText sx={{ fontSize: '1.1rem', color: '#777', mt: 0.5, lineHeight: 1.5 }}>
                            {subtitle}
                        </SingleLineText>
                    )}
                </Box>
            </Box>

            {/* Spacer for fixed header */}
            <Box sx={{ pt: subtitle ? '140px' : '110px', flexShrink: 0 }} />

            {/* Scrollable content with slide animation */}
            <Box ref={scrollRef} sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2 }}>
                <Box
                    key={step}
                    sx={{
                        animation: direction === 'forward'
                            ? 'wizardSlideInRight 0.25s ease-out'
                            : 'wizardSlideInLeft 0.25s ease-out',
                        '@keyframes wizardSlideInRight': {
                            from: { transform: 'translateX(24px)', opacity: 0 },
                            to: { transform: 'translateX(0)', opacity: 1 },
                        },
                        '@keyframes wizardSlideInLeft': {
                            from: { transform: 'translateX(-24px)', opacity: 0 },
                            to: { transform: 'translateX(0)', opacity: 1 },
                        },
                    }}
                >
                    {children}
                </Box>
            </Box>

            {/* Fixed bottom CTA — var(--keyboard-height)는 index.html이 visualViewport로 set */}
            {!ctaHidden && (
                <Box sx={{
                    // 시스템 nav bar/home indicator 영역은 native App.js의 AdContainer paddingBottom: insets.bottom이 처리.
                    // 여기서 env(safe-area-inset-bottom)을 또 빼면 안드로이드 WebView가 시스템 nav bar 인셋을
                    // 양수로 반환해 다음 버튼 아래 흰 여백이 생긴다 (iOS는 viewport-fit=cover 없어 영향 없음).
                    p: 2, pb: 'calc(12px + var(--keyboard-height, 0px))',
                    bgcolor: '#fff', flexShrink: 0,
                    transition: 'padding-bottom 0.15s ease',
                }}>
                    <AppButton
                        fullWidth
                        variant="contained"
                        disabled={ctaDisabled}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={onNext}
                        sx={{
                            py: 1.8, fontSize: '1.2rem',
                            boxShadow: ctaDisabled ? 'none' : '0 4px 12px rgba(230,168,0,0.3)',
                            transition: 'all 0.2s ease',
                        }}
                    >
                        {ctaLabel}
                    </AppButton>
                </Box>
            )}
        </Box>
    );
}
